import { LocationSource } from '@carvana/merch-geolocation';
import { useSearchParams } from 'next/navigation';
import { useContext, useEffect, useRef } from 'react';
import {
  browserCookieIdExperiments,
  userIdExperiments
} from '~/constants/vdp/experiments';
import { useTrackEvent } from '~/context/common/AnalyticsProvider';
import { useIsLoggedIn, useUserLocation } from '~/context/common/UserContext';
import UserContext from '~/context/common/UserContext';
import {
  useDeliveryInfo,
  usePickupInfo
} from '~/context/vdp/DeliveryPickupContext';
import StarRatingOwnerReviewContext, {
  useHasOwnerReviews
} from '~/context/vdp/StarRatingOwnerReviewContext';
import { UserVehicleContext } from '~/context/vdp/UserVehicleContext';
import {
  useGetVehicleDetails,
  useGetSpinnerData
} from '~/context/vdp/VehicleContext';
import useExperimentsData from '~/hooks/common/experiments/useExperimentsData';
import useDeliveryPickupLoadedOrWillNotLoad from '~/hooks/vdp/selectors/useDeliveryPickupLoadedOrWillNotLoad';
import usePaymentSummary from '~/hooks/vdp/selectors/useGetPaymentSummary';
import useGetPoseOEMData from '~/hooks/vdp/selectors/useGetPoseOEMData';
import useGetWhyBuyDataLoaded from '~/hooks/vdp/selectors/useGetWhyBuyDataLoaded';
import useIsNotAvailableForImmediatePurchase from '~/hooks/vdp/selectors/useIsNotAvailableForImmediatePurchase';
import useTransitChargeInfo from '~/hooks/vdp/selectors/useTransitChargeInfo';
import useWhyBuyData from '~/hooks/vdp/selectors/useWhyBuyData';
import useShowWhyBuyContent from '~/hooks/vdp/useShowWhyBuyContent';
import { useAvailabilityForAnalytics } from '../../useAvailabilityForAnalytics';

/**
 * This hook can send multiple events for one these actions:
 * - changing zip
 * - logging in via softpull modal or favorites
 * - favoriting/unfavoriting the vehicle
 * While not desired, these are expected and only the last event for the correlationId will be used for analytics.
 */
export default function useSendVDPMegaEvent() {
  const searchParams = useSearchParams();
  const isInitialLandingRef = useRef(true);
  const refSource = searchParams?.get('refSource') ?? undefined;
  const trackEvent = useTrackEvent();
  const { accuracyRadius, isFromCookies, source, zip } = useUserLocation();
  const { geolocationLogging: { merchLocationData = null } = {} } =
    useContext(UserContext);

  const isLoggedIn = useIsLoggedIn();
  const availabilityEventData = useAvailabilityForAnalytics();
  const {
    vehicleAvailability: { isLoaded: vehicleAvailabilityLoaded },
    vehiclePricingInfo: { data: pricingData, isLoaded: isPricingLoaded },
    isFavorite
  } = useContext(UserVehicleContext);
  const isDeliveryPickupLoadedOrWillNotLoad =
    useDeliveryPickupLoadedOrWillNotLoad();
  const {
    favorites: { isLoaded: isFavoritesLoaded }
  } = useContext(UserContext);
  const { experimentsWithBucket, experimentsLoaded } = useExperimentsData(
    browserCookieIdExperiments,
    userIdExperiments
  );
  const {
    hasOwnerReviews: { isLoaded: hasOwnerReviewsLoaded }
  } = useContext(StarRatingOwnerReviewContext);
  const { isLoaded: hasWhyBuyLoaded } = useGetWhyBuyDataLoaded();
  const { type: whyBuyType, bulletPoints = [] } = useWhyBuyData();
  const hasOwnerReviews = useHasOwnerReviews();
  const showWhyBuyContent = useShowWhyBuyContent();
  const { showPoseOEMData } = useGetPoseOEMData();
  const { imperfections = [] } = useGetSpinnerData();
  const imperfectionCount = imperfections.length;
  const transitChargeInfo = useTransitChargeInfo();
  const {
    factoryUpgrades,
    location: { locationId: vehicleLocationId, zip5: vehicleLocationZip } = {},
    brochureUrl,
    isOEMFirstVehicle = false,
    saleStatus,
    stores = [],
    windowStickerUrl
  } = useGetVehicleDetails();
  const delivery = useDeliveryInfo();
  const pickup = usePickupInfo();
  const {
    totals: { totalCashDue },
    financing: { apr }
  } = usePaymentSummary();

  const isAvailableForImmediatePurchase =
    !useIsNotAvailableForImmediatePurchase();

  const isAllDataLoaded =
    vehicleAvailabilityLoaded &&
    isPricingLoaded &&
    isDeliveryPickupLoadedOrWillNotLoad &&
    hasOwnerReviewsLoaded &&
    hasWhyBuyLoaded &&
    isFavoritesLoaded &&
    experimentsLoaded &&
    transitChargeInfo.isLoaded;

  useEffect(() => {
    if (!isAllDataLoaded) {
      return;
    }

    const loadTime = isInitialLandingRef.current
      ? Math.round(performance.now())
      : null;

    trackEvent(
      'mega-vdp',
      {
        apr,
        availabilityData: availabilityEventData,
        coApplicantToggleOn: pricingData?.financeInfo?.coApplicantToggleOn,
        delivery,
        downPayment: pricingData?.price?.downPayment ?? 0,
        experiments: experimentsWithBucket,
        factoryUpgrades,
        financingType: pricingData?.financeInfo?.financingType,
        geoIpAccuracyRadius:
          source === 'ipaddress'
            ? accuracyRadius
            : source === LocationSource.UtmLocation
            ? merchLocationData?.accuracyRadius ?? null
            : null,
        hasBrochure: Boolean(brochureUrl),
        hasCoApplicant: pricingData?.financeInfo?.hasCoApplicant,
        hasPoseOemData: showPoseOEMData,
        hasOwnerReviews,
        hasWhyBuy: showWhyBuyContent,
        hasWindowSticker: Boolean(windowStickerUrl),
        imperfectionCount,
        isAvailableForImmediatePurchase,
        isFavorited: isFavorite,
        isInitialLanding: isInitialLandingRef.current,
        isLoggedIn,
        isOEMFirstVehicle,
        isSold: saleStatus === 'Sold',
        isTradeInApplied: pricingData?.tradeInfo?.applyTradeIn ?? null,
        isZipFromCookies: isFromCookies,
        loadTime,
        pickup,
        refSource,
        stores,
        totalCashDue,
        transitCharge: transitChargeInfo.charge,
        vehicleLocationId,
        vehicleLocationZip,
        whyBuyCount: bulletPoints?.length,
        whyBuyType,
        zip,
        zipFromIp: merchLocationData?.zip ?? null,
        zipSource: source
      },
      undefined,
      {
        useFetch: true,
        fetchOption: { keepalive: false }
      }
    );

    isInitialLandingRef.current = false;
  }, [
    accuracyRadius,
    apr,
    availabilityEventData,
    brochureUrl,
    bulletPoints?.length,
    delivery,
    experimentsWithBucket,
    factoryUpgrades,
    hasOwnerReviews,
    imperfectionCount,
    isAllDataLoaded,
    isAvailableForImmediatePurchase,
    isFavorite,
    isFromCookies,
    isLoggedIn,
    isOEMFirstVehicle,
    merchLocationData?.accuracyRadius,
    merchLocationData?.zip,
    pickup,
    pricingData?.financeInfo?.coApplicantToggleOn,
    pricingData?.financeInfo?.financingType,
    pricingData?.financeInfo?.hasCoApplicant,
    pricingData?.price?.downPayment,
    pricingData?.tradeInfo?.applyTradeIn,
    refSource,
    saleStatus,
    showPoseOEMData,
    showWhyBuyContent,
    source,
    stores,
    totalCashDue,
    trackEvent,
    transitChargeInfo.charge,
    vehicleLocationId,
    vehicleLocationZip,
    whyBuyType,
    windowStickerUrl,
    zip
  ]);

  return null;
}
